// COPIED from ptc-core web/src/feedback/feedbackContext.ts @ de39fff (fleet feedback widget
// contract v1, 2026-10-21: ptc-docs/coordination/plans/2026-10-21-fleet-feedback-widget-contract.md).
// Changes from the source: `buildSha` reads this app's own build stamp (`buildInfo.commit`, see
// src/buildInfo.ts) instead of a separate BUILD_ID constant; `recentErrors` is always `[]` -- this
// app has no client-error ring yet (see components/ErrorBoundary.tsx, which has no reporting path
// wired to it), and building one is out of scope for the feedback widget itself. Conformance:
// FeedbackWidgetConformance.test.tsx.
//
// ⚠ THIS IS A DATA EGRESS POINT. Two rules, both server-enforced again on the receiving end:
//   - the ROUTE keeps its query string, because filter/view state lives there and a developer must
//     open exactly what the reporter saw -- BUT free-text search parameters are stripped:
//     `?q=John+Smith` is a client's name, not view state;
//   - if a client-error ring is ever added here, it must carry only PII-free fields (name,
//     message, pathname, timestamp), same shape as ptc-core's reportClientError.ts.
//
// `uiLanguage` added per contract v1.1 / C10 (ruled 2026-10-27, ptc-docs 5a792fc6): the server
// renders its own reporter-facing text (validation/refusal messages) in this language; the typed
// report `text` itself is never touched. Normalized to the same short tag LanguageToggle.tsx uses
// ('es' or 'en'), not i18next's raw BCP-47 value.

import i18n from '../i18n';
import { buildInfo } from '../buildInfo';

/** Query parameters that carry typed text rather than view state. */
export const FREE_TEXT_PARAMS = ['q', 'search', 'query', 'term'] as const;

export interface FeedbackRecentError {
  name: string;
  message: string;
  pathname: string;
  timestamp: string;
}

export interface FeedbackContext {
  route: string;
  buildSha: string;
  viewport: string;
  userAgent: string;
  clientTime: string;
  tz: string;
  recentErrors: FeedbackRecentError[];
  uiLanguage: string;
}

/** `pathname + search`, minus every free-text parameter. */
export function routeWithoutFreeText(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  for (const key of [...params.keys()]) {
    if ((FREE_TEXT_PARAMS as readonly string[]).includes(key.toLowerCase())) params.delete(key);
  }
  const rest = params.toString();
  return rest ? `${pathname}?${rest}` : pathname;
}

export function buildFeedbackContext(now: Date = new Date()): FeedbackContext {
  return {
    route: routeWithoutFreeText(window.location.pathname, window.location.search),
    buildSha: buildInfo.commit ?? 'local',
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    userAgent: navigator.userAgent,
    clientTime: now.toISOString(),
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'unknown',
    recentErrors: [],
    uiLanguage: (i18n.resolvedLanguage ?? i18n.language ?? 'en').startsWith('es') ? 'es' : 'en',
  };
}
